import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import L from 'leaflet';
import { getJobsiteCoords } from './useJobsite';

/**
 * Manages the jobsite geofence radius + center + renders the circle on the map.
 * When repositioning is active, shows a draggable center marker.
 */
export default function useGeofence({ url, jobsite, mapRef, visible = true }) {
  const [radius, setRadius] = useState(200);
  const [center, setCenter] = useState(null); // { lat, lng } override or null = jobsite coords
  const [repositioning, setRepositioning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const circleLayerRef = useRef(null);
  const markerRef = useRef(null);

  // Initialize from jobsite
  useEffect(() => {
    setRadius(jobsite?.radius || 200);
    setCenter(jobsite?.geofenceCenter || null);
    setConfirmed(!!jobsite?.geofenceConfirmed);
  }, [jobsite]);

  // Effective center: custom override or jobsite location
  const getEffectiveCenter = useCallback(() => {
    if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
      return [center.lat, center.lng];
    }
    const fallback = getJobsiteCoords(jobsite);
    if (Array.isArray(fallback) && Number.isFinite(fallback[0]) && Number.isFinite(fallback[1])) {
      return fallback;
    }
    return null;
  }, [center, jobsite]);

  // Render / update the circle on the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clean up existing layers
    if (circleLayerRef.current) {
      try { map.removeLayer(circleLayerRef.current); } catch (_) {}
      circleLayerRef.current = null;
    }
    if (markerRef.current) {
      try { map.removeLayer(markerRef.current); } catch (_) {}
      markerRef.current = null;
    }

    if (!visible || !jobsite?._id) return;

    const coords = getEffectiveCenter();
    if (!coords) return;

    // Bright yellow dashed stroke for high visibility on light/desert and dark/aerial tiles.
    // Keep option shape identical to the original working circle — Leaflet.Geoman is
    // auto-attached to every L.circle in this app and crashes on unusual option combos.
    const circle = L.circle(coords, {
      radius: radius || 200,
      color: '#FFD400',
      weight: 4,
      opacity: 1,
      fillColor: '#FFD400',
      fillOpacity: 0.08,
      dashArray: '10, 6',
      interactive: false,
    });
    circle.addTo(map);
    circleLayerRef.current = circle;

    // When repositioning, add a draggable marker at center
    if (repositioning) {
      const marker = L.marker(coords, {
        draggable: true,
        icon: L.divIcon({
          className: '',
          html: '<div style="width:20px;height:20px;border-radius:50%;background:#1565c0;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);cursor:grab;"></div>',
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
      });
      marker.on('drag', (e) => {
        const pos = e.target.getLatLng();
        if (circleLayerRef.current) {
          circleLayerRef.current.setLatLng(pos);
        }
      });
      marker.on('dragend', (e) => {
        const pos = e.target.getLatLng();
        setCenter({ lat: pos.lat, lng: pos.lng });
      });
      marker.addTo(map);
      markerRef.current = marker;
    }

    return () => {
      if (circleLayerRef.current) {
        try { map.removeLayer(circleLayerRef.current); } catch (_) {}
        circleLayerRef.current = null;
      }
      if (markerRef.current) {
        try { map.removeLayer(markerRef.current); } catch (_) {}
        markerRef.current = null;
      }
    };
  }, [jobsite, radius, center, visible, repositioning, mapRef, getEffectiveCenter]);

  // Save radius and/or center to backend
  const saveRadius = async (newRadiusMeters) => {
    if (!url || !jobsite?._id) return;
    setRadius(newRadiusMeters);
    setSaving(true);
    try {
      const resp = await axios.put(
        `${url}/api/customer/jobsite/${jobsite._id}/geofence`,
        { radius: newRadiusMeters }
      );
      if (resp.data?.success) {
        setRadius(resp.data.radius);
      }
    } catch (err) {
      console.error('saveRadius error:', err);
    } finally {
      setSaving(false);
    }
  };

  const saveCenter = async (newCenter) => {
    if (!url || !jobsite?._id) return;
    setSaving(true);
    try {
      const resp = await axios.put(
        `${url}/api/customer/jobsite/${jobsite._id}/geofence`,
        { geofenceCenter: newCenter }
      );
      if (resp.data?.success) {
        setCenter(resp.data.geofenceCenter || null);
      }
    } catch (err) {
      console.error('saveCenter error:', err);
    } finally {
      setSaving(false);
    }
  };

  const resetCenter = async () => {
    if (!url || !jobsite?._id) return;
    setCenter(null);
    setSaving(true);
    try {
      await axios.put(
        `${url}/api/customer/jobsite/${jobsite._id}/geofence`,
        { geofenceCenter: null }
      );
    } catch (err) {
      console.error('resetCenter error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Confirm geofence — explicit "I've reviewed this radius/center, it's good"
  const confirmGeofence = async () => {
    if (!url || !jobsite?._id) return;
    setSaving(true);
    try {
      const resp = await axios.put(
        `${url}/api/customer/jobsite/${jobsite._id}/geofence`,
        { geofenceConfirmed: true }
      );
      if (resp.data?.success) {
        setConfirmed(true);
      }
    } catch (err) {
      console.error('confirmGeofence error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Start/confirm/cancel repositioning
  const startRepositioning = () => setRepositioning(true);

  const confirmRepositioning = async () => {
    setRepositioning(false);
    if (center) await saveCenter(center);
  };

  const cancelRepositioning = () => {
    setRepositioning(false);
    setCenter(jobsite?.geofenceCenter || null);
  };

  return {
    radius,
    center,
    repositioning,
    saving,
    confirmed,
    hasCustomCenter: !!center,
    saveRadius,
    confirmGeofence,
    startRepositioning,
    confirmRepositioning,
    cancelRepositioning,
    resetCenter,
  };
}
